import { cn } from "@istari-frontend/shared";
import { Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { AlertTriangle, Check, ChevronDown, ChevronUp, X, XCircle } from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { BarsRotateFade } from "~/assets/svg";
import { FileTypeIcon } from "~/components/file-type-icon";
import { buildResourcePath } from "~/routes";

import {
  dismissUploadBatch,
  type FileUploadState,
  fileUploadState,
  getUploadBatch,
  retryOperations,
  subscribeUploadBatch,
  type UploadingFile,
  type UploadProgress,
} from "./upload-batch-progress";
import { UploadOperationControls } from "./upload-operation-controls";

export function subscribeUploadBatchPresenter() {
  let toastId: string | null = null;
  let collapsed = false;
  const render = () => {
    const batch = getUploadBatch();
    if (toastId && toastId !== batch?.id) {
      toast.dismiss(toastId);
      collapsed = false;
    }
    toastId = batch?.id ?? null;
    if (!batch?.progress) return;
    const progress = batch.progress;
    const isCollapsed = collapsed;
    toast.custom(
      () => (
        <ToastContent
          progress={progress}
          collapsed={isCollapsed}
          toggle={() => {
            collapsed = !collapsed;
            render();
          }}
        />
      ),
      { id: batch.id, duration: Infinity, dismissible: false },
    );
  };
  return subscribeUploadBatch(render);
}

function ToastContent({
  progress,
  collapsed,
  toggle,
}: {
  progress: UploadProgress;
  collapsed: boolean;
  toggle: () => void;
}) {
  const { files, completedCount, totalCount, overallState } = progress;
  return (
    <div
      className="bg-popover text-popover-foreground border-border flex w-80 flex-col gap-2 rounded-lg border p-3 shadow-lg"
      data-testid="upload-progress-toast"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 pl-1">
          <StatusIcon state={overallState} />
          <span className="truncate text-sm font-medium tabular-nums">
            {getHeaderText(overallState, totalCount, completedCount)}
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={toggle}
            className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-10 items-center justify-center rounded transition-colors duration-200 ease-out"
            aria-label={collapsed ? "Expand uploads" : "Collapse uploads"}
          >
            {collapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </button>
          {overallState !== "uploading" && (
            <button
              type="button"
              onClick={dismissUploadBatch}
              className="text-muted-foreground hover:text-foreground hover:bg-accent flex size-10 items-center justify-center rounded transition-colors duration-200 ease-out"
              aria-label="Dismiss"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>
      {overallState === "uploading" && (
        <p className="text-muted-foreground text-xs">
          Uploads continue while collapsed. Expand to view progress or cancel.
        </p>
      )}
      {!collapsed && (
        <div className="max-h-48 space-y-0.5 overflow-y-auto">
          {files.map((file) => (
            <FileRow key={file.id} file={file} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ state }: { state: UploadProgress["overallState"] | FileUploadState }) {
  if (state === "uploading" || state === "pending") {
    return <BarsRotateFade className="fill-primary size-4 flex-shrink-0" />;
  }
  if (state === "success") return <SuccessCheck className="size-4 flex-shrink-0" />;
  if (state === "error") return <XCircle className="text-destructive size-4 flex-shrink-0" />;
  if (state === "partial") return <AlertTriangle className="text-warning size-4 flex-shrink-0" />;
  return null;
}

function FileRow({ file }: { file: UploadingFile }) {
  const navigate = useNavigate();
  const state = fileUploadState(file);
  const isClickable = state === "success" && Boolean(file.resourceId);
  const handleNavigate = () => {
    if (!isClickable || !file.resourceId) return;
    navigate(buildResourcePath({ resourceId: file.resourceId }));
  };
  return (
    <div
      className={cn("group/row flex items-center gap-2 rounded px-2 py-1.5 text-xs", isClickable && "hover:bg-accent")}
      onClick={handleNavigate}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleNavigate();
        }
      }}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <FileTypeIcon extension={file.extension} className="text-foreground size-4 flex-shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        <span className="text-foreground">{file.displayName}</span>
        <span className="text-muted-foreground">.{file.extension}</span>
      </span>
      {state === "error" && file.errorMessage && (
        <span className="text-muted-foreground flex-shrink-0 truncate text-xs" title={file.errorMessage}>
          Failed
        </span>
      )}
      {state !== "success" && (
        <UploadOperationControls
          operationId={file.id}
          snapshot={
            file.snapshot ?? (file.state === "error" ? { state: "error", canResume: false } : { state: file.state })
          }
          retry={retryOperations.get(file.id)}
        />
      )}
      <span className="relative flex size-4 flex-shrink-0 items-center justify-center">
        {isClickable ? (
          <>
            <span
              className="absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)] group-hover/row:opacity-0"
              aria-hidden="true"
            >
              <FileStateIcon state={file.state} />
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="text-muted-foreground absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 ease-[cubic-bezier(0.2,0,0,1)] group-hover/row:opacity-100"
                  aria-label="Go to this resource"
                >
                  <FileTypeIcon extension={file.extension} className="size-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">Go to this resource</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <FileStateIcon state={file.state} />
        )}
      </span>
    </div>
  );
}

function FileStateIcon({ state }: { state: FileUploadState }) {
  if (state === "pending") {
    return <span className="text-muted-foreground inline-block size-3 rounded-full border border-current" />;
  }
  if (state === "uploading") return <BarsRotateFade className="fill-primary size-3" />;
  if (state === "success") return <SuccessCheck className="size-4" />;
  if (state === "error") return <XCircle className="text-destructive size-4" />;
  return null;
}

function SuccessCheck({ className }: { className?: string }) {
  return (
    <span className={cn("bg-success flex items-center justify-center rounded-full", className)} aria-hidden="true">
      <Check className="text-background size-2.5" strokeWidth={3} />
    </span>
  );
}

function getHeaderText(state: UploadProgress["overallState"], totalCount: number, completedCount: number): string {
  switch (state) {
    case "uploading":
      return `Uploading ${completedCount} of ${totalCount}…`;
    case "success":
      return `${totalCount} upload${totalCount === 1 ? "" : "s"} complete`;
    case "error":
      return `Failed to upload ${totalCount} file${totalCount === 1 ? "" : "s"}`;
    case "cancelled":
      return `${totalCount} upload${totalCount === 1 ? "" : "s"} cancelled`;
    case "partial":
      return `Uploads finished with mixed results`;
    default:
      return "";
  }
}
